import { useState } from 'react';
import { useAuthContext } from '../../hooks/auth';
import { useClearAppCachesOnMount } from '../../hooks/useClearAppCachesOnMount';
import { Helmet } from 'react-helmet-async';
import { displaySuccess, displayWarning } from "../../utils/messageToast";
import { createSkills } from '../../functions/createSkils';
import { TextAreaErrors } from '../../components/ui/textArea';
import Editor from '@monaco-editor/react';
import { ButtonCopyReturn, ButtonCreateSkills } from '../../components/ui/buttons';
import Modal from '../../components/ui/modal';

export default function CreateSkills() {
  const { authorization, authorizationForce, user } = useAuthContext();
  const [isInForce, setIsInForce] = useState(localStorage.getItem("inForce") === "true" ? true : false);
  const ambient = user?.ambient as string;
  const [loadingButton, setLoadingButton] = useState(false);
  const [namesData, setNamesData] = useState<any[]>([]);
  const [textAreaValue, setTextAreaValue] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isErrors, setIsErrors] = useState(false);
  const [errors, setErrors] = useState<any[]>([]);

  useClearAppCachesOnMount();

  const handleTextAreaValue = async (value: string) => {
    const lines = value.split('\n').map(line => ({ name: line }));
    setNamesData(lines);
  }

  const handleCreateSkills = async () => {
    setLoadingButton(true);
    if (isInForce) {
      await createSkills(ambient, authorizationForce, namesData, setTextAreaValue, setIsErrors, setErrors, isInForce);
    } else {
      await createSkills(ambient, authorization, namesData, setTextAreaValue, setIsErrors, setErrors, isInForce);
    }
    setLoadingButton(false);
    setIsModalOpen(false);
  };

  return (
    <div className="bg-page">
      <Helmet>
        <title>Criar habilidades em massa</title>
      </Helmet>

      <div className="bg-page-create-form-payload w-full px-2 sm:px-4 md:px-6 py-4 payload-page payload-page--skills">
        <div className="form-page-shell">
          <div className="form-config-card">
            <div className="form-card-header">
              <div>
                <h2 className="form-card-title">Criar habilidades em massa</h2>
                <p className="form-card-subtitle">Insira uma habilidade por linha</p>
              </div>
            </div>

            <div className="form-card-body">
              <div className="form-payload-actions is-start">
                <ButtonCreateSkills
                  loadingButtonCreate={loadingButton}
                  type="button"
                  disabled={loadingButton}
                  onClick={() => {
                    if (textAreaValue === '') {
                      displayWarning("Adicione as habilidades no campo de texto!");
                      return;
                    }
                    setIsModalOpen(true)
                  }}
                />

                <ButtonCopyReturn
                  onClick={() => {
                    if (textAreaValue === '') {
                      displayWarning("Nenhuma informação para copiar!");
                      return;
                    }
                    navigator.clipboard.writeText(textAreaValue);
                    displaySuccess("Retorno copiado para a área de transferência!");
                  }}
                />
              </div>

              {isModalOpen && (
                <Modal
                  isOpen={isModalOpen}
                  loading={loadingButton}
                  onConfirm={handleCreateSkills}
                  onClose={() => setIsModalOpen(false)}
                >
                  <p className="mb-5 text-base sm:text-lg">Confirma a criação das habilidades?</p>
                </Modal>
              )}
            </div>

          </div>

          <div className="form-payload-card">
            <div className="form-payload-header">
              <h3 className="form-card-title">Habilidades</h3>
              <p className="form-card-subtitle">Cada linha será uma habilidade</p>
            </div>
            <div className="form-payload-body">
              <div className="form-payload-editor">
                <Editor
                  height="360px"
                  language="text"
                  value={textAreaValue}
                  onChange={(value) => {
                    const next = value ?? '';
                    setTextAreaValue(next);
                    handleTextAreaValue(next);
                  }}
                  theme="vs-light"
                  options={{
                    minimap: { enabled: false },
                    fontSize: 13,
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    wordWrap: 'on',
                    tabSize: 2,
                    automaticLayout: true,
                  }}
                />
              </div>
            </div>
          </div>

          {isErrors && (
            <div className="form-payload-card">
              <div className="form-payload-header">
                <h3 className="form-card-title">Erros</h3>
                <p className="form-card-subtitle">Itens que falharam na criação</p>
              </div>
              <div className="form-payload-body">
                <TextAreaErrors
                  value={errors}
                  readOnly
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

